import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline, Delete, CheckCircle } from "@mui/icons-material";
import { Search, X } from "lucide-react";
import { MoreVert } from "@mui/icons-material";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { EditIcon } from "../comps/CustomIcons";
import { useGetApi } from "../hooks/useGetApi";
import { getRolesApi, addRoleApi, updateRoleApi, deleteRoleApi } from "../services/role.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";

// Available permissions for role assignment
const AVAILABLE_PERMISSIONS = [
  // Module Access
  "Dashboard", "Consultation", "Patients", "Appointments", "Nursing", 
  "Pharmacy", "Billing & Payments", "Reports", "User", "Settings", "Role", "Master",
  
  // CRUD Operations
  "Patients.Create", "Patients.Read", "Patients.Update", "Patients.Delete",
  "Users.Create", "Users.Read", "Users.Update", "Users.Delete",
  "Appointments.Create", "Appointments.Read", "Appointments.Update", "Appointments.Delete",
  "Consultations.Create", "Consultations.Read", "Consultations.Update", "Consultations.Delete",
  "Pathology.Create", "Pathology.Read", "Pathology.Update", "Pathology.Delete",
  "Radiology.Create", "Radiology.Read", "Radiology.Update", "Radiology.Delete",
  "Suppliers.Create", "Suppliers.Read", "Suppliers.Update", "Suppliers.Delete",
  "Store.Create", "Store.Read", "Store.Update", "Store.Delete",
  "Roles.Create", "Roles.Read", "Roles.Update", "Roles.Delete",
];

const headLabel = [
  { id: 1, label: "Role", value: "role" },
  { id: 2, label: "Permissions", value: "permissions" },
  { id: 3, label: "Active", value: "active" },
  { id: 4, label: "Actions", value: "actions" },
];

export default function MasterRole() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("role");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  
  // Form states
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isActive, setIsActive] = useState(true);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get roles from API (backend doesn't support search, so we'll filter client-side)
  const {
    dataList: allRoles,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getRolesApi,
    params: {},
    dependencies: [],
    debounceDelay: 500,
  });

  // Client-side filtering for roles
  const [filteredRoles, setFilteredRoles] = useState([]);

  useEffect(() => {
    if (!allRoles || allRoles.length === 0) {
      setFilteredRoles([]);
      return;
    }

    if (!search) {
      setFilteredRoles(allRoles);
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = allRoles.filter((role) => {
      if (searchBy === "role") {
        return role.role?.toLowerCase().includes(searchLower);
      }
      return true;
    });
    setFilteredRoles(filtered);
  }, [allRoles, search, searchBy]);

  // Use filteredRoles for display
  const roles = filteredRoles;

  const handleEdit = (rowData) => {
    console.log('Edit clicked for role:', rowData);
    setSelectedRow(rowData);
    setRoleName(rowData.role);
    setSelectedPermissions(rowData.permissions || []);
    setIsActive(rowData.active !== false);
    setModalOpen(true);
  };

  const handleView = (rowData) => {
    console.log('View clicked for role:', rowData);
    setSelectedRow(rowData);
    setViewModalOpen(true);
  };

  const handleDelete = (rowData) => {
    console.log('Delete clicked for role:', rowData);
    setSelectedRow(rowData);
    setDeleteDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }

    setLoading(true);
    try {
      const roleData = {
        role: roleName,
        permissions: selectedPermissions,
        active: isActive,
        description: `${roleName} role with ${selectedPermissions.length} permissions`
      };

      console.log('=== Frontend Role Save Debug ===');
      console.log('Role data being sent:', roleData);
      console.log('Is editing:', !!selectedRow);
      console.log('Selected permissions:', selectedPermissions);

      let response;
      if (selectedRow) {
        // Update existing role
        console.log('Updating role with ID:', selectedRow._id);
        response = await updateRoleApi(selectedRow._id, roleData);
      } else {
        // Create new role
        console.log('Creating new role');
        response = await addRoleApi(roleData);
      }

      console.log('API Response:', response);

      if (response?.status === 200 || response?.code === 200 || response?.isSuccess) {
        toast.success(`Role ${selectedRow ? 'updated' : 'created'} successfully`);
        setModalOpen(false);
        resetForm();
        refetch();
      } else {
        console.error('API Error Response:', response);
        toast.error(response?.message || `Failed to ${selectedRow ? 'update' : 'create'} role`);
      }
    } catch (error) {
      console.error("Save role error:", error);
      toast.error(`Failed to ${selectedRow ? 'update' : 'create'} role: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    
    setLoading(true);
    try {
      const response = await deleteRoleApi(selectedRow._id);
      if (response?.code === 200) {
        toast.success("Role deleted successfully");
        setDeleteDialogOpen(false);
        setSelectedRow(null);
        refetch();
      } else {
        toast.error(response?.message || "Failed to delete role");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete role");
    } finally {
      setLoading(false);
    }
  };


  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    navigate(-1);
  };

  const resetForm = () => {
    setSelectedRow(null);
    setRoleName("");
    setSelectedPermissions([]);
    setIsActive(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    resetForm();
  };

  const handlePermissionToggle = (permission) => {
    console.log(`Toggling permission: ${permission}`);
    console.log('Current selected permissions:', selectedPermissions);
    
    setSelectedPermissions(prev => {
      const newPermissions = prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission];
      
      console.log('New selected permissions:', newPermissions);
      return newPermissions;
    });
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  // Prepare rows for DataGrid
  const rows = roles?.map((row) => {
    return {
      id: row._id,
      _id: row._id,
      role: row.role || "-",
      permissions: row.permissions || [],
      active: row.active !== false ? "Active" : "Inactive",
      isSystemRole: row.isSystemRole || false,
      permissionsCount: (row.permissions || []).length,
    };
  }) || [];

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        field: 'role',
        headerName: 'Role',
        flex: isMobile ? 0 : 1,
        width: isMobile ? 150 : undefined,
        minWidth: 150,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{params.value || "-"}</Typography>
            {(params.row.role === 'Super Admin' || params.row.isSystemRole) && (
              <Chip label="System" size="small" color="warning" />
            )}
          </Box>
        ),
      },
      {
        field: 'permissions',
        headerName: 'Permissions',
        flex: isMobile ? 0 : 2,
        width: isMobile ? 200 : undefined,
        minWidth: 200,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {params.value && params.value.length > 0 ? (
              params.value.slice(0, 3).map((perm, idx) => (
                <Chip key={idx} label={perm} size="small" variant="outlined" />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">No permissions</Typography>
            )}
            {params.value && params.value.length > 3 && (
              <Chip 
                label={`+${params.value.length - 3} more`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Box>
        ),
      },
      {
        field: 'active',
        headerName: 'Active',
        flex: isTablet ? 0 : 0.6,
        width: isTablet ? 100 : undefined,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={params.value === "Active" ? "success" : "default"}
            icon={params.value === "Active" ? <CheckCircle /> : null}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.4,
        minWidth: 80,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="actions"
            size={isMobile ? "small" : "medium"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e, params.row);
            }}
          >
            <MoreVert fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        ),
      },
    ];

    // Responsive column hiding
    if (isMobile) {
      return baseColumns.filter(col => 
        ['role', 'active', 'actions'].includes(col.field)
      );
    }
    if (isTablet) {
      return baseColumns.filter(col => 
        ['role', 'permissions', 'active', 'actions'].includes(col.field)
      );
    }
    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search and Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={{ 
              color: "#5D6A82", 
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "22px",
              lineHeight: "29px",
              letterSpacing: "0px"
            }}>Role Master</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "20px",
              marginBottom: "40px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by Role"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  display: "flex",
                  gap: 0.5,
                }}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  display: "flex",
                  gap: 0.5,
                  minWidth: "150px",
                }}
                onClick={() => {
                  console.log('New Role button clicked');
                  resetForm();
                  setModalOpen(true);
                }}
              >
                <AddCircleOutline />
                <Typography sx={{ color: "white" }}>New Role</Typography>
              </Button>
            </Box>
          </Box>

          {/* Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" },
                width: '100%',
                minHeight: '300px',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="client"
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    width: '100%',
                  },
                  '& .MuiDataGrid-main': {
                    width: '100%',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: isMobile ? '4px' : '8px',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    '& .MuiDataGrid-columnHeader': {
                      fontSize: '11px',
                      padding: '4px',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '11px',
                      padding: '2px 4px',
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No roles found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { handleView(selectedRow); handleMenuClose(); }}>
              <RemoveRedEyeOutlinedIcon sx={{ mr: 1 }} />
              View Permissions
            </MenuItem>
            <MenuItem onClick={() => { handleEdit(selectedRow); handleMenuClose(); }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Role
            </MenuItem>
            {selectedRow?.role !== 'Super Admin' && !selectedRow?.isSystemRole && (
              <MenuItem onClick={() => { handleDelete(selectedRow); handleMenuClose(); }} sx={{ color: 'red' }}>
                <Delete sx={{ mr: 1 }} />
                Delete Role
              </MenuItem>
            )}
          </Menu>

          {/* Role Create/Edit Modal */}
          <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
            <DialogTitle>
              {selectedRow ? `Edit Role: ${selectedRow.role}` : "Create New Role"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  disabled={selectedRow?.role === 'Super Admin' || selectedRow?.isSystemRole}
                  sx={{ mb: 2 }}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  }
                  label="Active"
                  sx={{ mb: 2 }}
                />

                <Typography variant="h6" gutterBottom>
                  Permissions ({selectedPermissions.length} selected)
                  {selectedPermissions.length > 0 && (
                    <Button 
                      size="small" 
                      onClick={() => setSelectedPermissions([])}
                      sx={{ ml: 2, textTransform: 'none' }}
                    >
                      Clear All
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    onClick={() => setSelectedPermissions([...AVAILABLE_PERMISSIONS])}
                    sx={{ ml: 1, textTransform: 'none' }}
                  >
                    Select All
                  </Button>
                </Typography>
                
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                  <Grid container spacing={1}>
                    {AVAILABLE_PERMISSIONS.map((permission) => (
                      <Grid item xs={12} sm={6} key={permission}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(permission)}
                              onChange={() => {
                                console.log(`Toggling permission: ${permission}`);
                                handlePermissionToggle(permission);
                              }}
                              color="primary"
                            />
                          }
                          label={permission}
                          sx={{ 
                            width: '100%', 
                            mx: 0,
                            backgroundColor: selectedPermissions.includes(permission) ? '#f0f8ff' : 'transparent',
                            borderRadius: 1
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* Selected Permissions Summary */}
                {selectedPermissions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Permissions:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 100, overflow: 'auto' }}>
                      {selectedPermissions.map((permission) => (
                        <Chip
                          key={permission}
                          label={permission}
                          size="small"
                          onDelete={() => handlePermissionToggle(permission)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleSaveRole}
                disabled={loading || !roleName.trim()}
              >
                {loading ? "Saving..." : (selectedRow ? "Update" : "Create")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* View Permissions Modal */}
          <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Role: {selectedRow?.role}
              {(selectedRow?.role === 'Super Admin' || selectedRow?.isSystemRole) && (
                <Chip label="System Role" size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" gutterBottom>
                Permissions ({selectedRow?.permissions?.length || 0}):
              </Typography>
              <List dense>
                {selectedRow?.permissions?.length > 0 ? (
                  selectedRow.permissions.map((permission, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={permission} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No permissions assigned" />
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewModalOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete the role "{selectedRow?.role}"?
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
}
